/**
 * Email-Automatisierungen — Entscheidungs-Ansicht (decision board).
 *
 * Vier Segment-Views:
 *   - Entscheiden  : KPI-Leiste + 4 Gruppen (Voraussetzungen / Jetzt entscheidbar /
 *                    Vorbereitet / Geblockt) als Card-Zeilenlisten mit FESTEN
 *                    Grid-Spalten (Badges/Pills stehen exakt untereinander).
 *   - Familien     : Callout + Familien-Cards (Detail-L2) + Pilot-Status-Zeile.
 *   - Vorfaelle    : Vorfall-Ketten (Mehr-Thread-Vorgaenge) — KPI-Leiste +
 *                    Zeilenliste, Klick klappt Thread-Subjects chronologisch auf.
 *   - Agent        : Callout + Stacked-Bar + 3 Stufen-Zeilen; Stufen sind
 *                    drillbar → L2 agent-stufe-N mit allen Clustern der Stufe.
 * L2 (Route /email-automation/decide/:cardId): Karten-Detail (Quick-Win /
 *   Prereq / Familie / Agent-Stufe). "Entscheiden & zuweisen" (DecisionBlock)
 *   legt einen echten Atlas-Task an und persistiert die Entscheidung
 *   serverseitig (Tabelle email_automation_decision, NICHT im Board-JSON).
 *
 * Datenquelle: GET /email-automation/decision-board (statisches kuratiertes JSON,
 * WH-gegatet wie der restliche Router). Ein Fetch, im State gehalten.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RotateCcw,
  Star,
  UserPlus,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { Card, Field, Input, Modal, Select, Textarea } from "../ui";
import { useToast } from "../ui/toast";
import { useGermanDateField, DE_DATE_HINT } from "../ui/germanDateField";
import { UserPicker, type Person } from "../components/UserPicker";
import { EntityPage, KpiChip, BackButton, docLabel, akteurLabel, keyActivate } from "./emailAutomationEntity";

// Genau 2 Top-Level-Views (P1.2/P1.3): Entscheiden & Steuern + Agent-Roadmap.
// Prozess-Familien = Detailebene unter Entscheiden; Vorfall-Ketten = Beleg-Block
// in der Familien-L2; Dokumente = Kontext-Panel in der Karten-L2.
export type BoardView = "entscheiden" | "agent";

// ── Board-Typen (Spiegel decision_board.yaml) ────────────────────────────────
interface Kpis {
  tasks: number; threads: number; clusters: number; use_cases: number;
  vorfaelle: number; emails: number; total_saved_hours_6mo: number;
  hours_by_lever: Record<string, number>;
  hours_by_dept: Record<string, number>;
}
interface Prereq {
  id: string; platform: string; titel: string; beschreibung_kurz: string;
  effort: string; blocking: string; status: string;
  unlock_hours_per_year?: number;
  unlocks?: { quick_wins?: string[]; patterns?: string[]; agent_stufe2_cluster?: number };
}
// Ein SF-verlinktes Dokument (Id ist SF-Record-Id -> {SF_BASE}/{id} redirected).
interface DocRef { id: string; name: string; typ?: string; sf_url: string; thread_id?: string }
interface Bestand { was: string; evidenz?: string }
interface Luecke { was: string; effort?: string }
interface Entscheidung { frage: string; optionen?: string[] }
interface QuickWin {
  id: string; titel: string; plattform: string; pattern: string; triage: string;
  volumen?: Record<string, unknown>;
  bestand?: Bestand[]; luecke?: Luecke[]; entscheidungen_offen?: Entscheidung[];
  prerequisites?: string[]; risiko?: string; effort_gesamt: string; status: string;
}
interface ChecklistItem { item: string; anteil: number }
interface FamilieBeispiel {
  thread_id?: string; sf_url?: string; trigger?: string; trigger_evidence?: string;
  outcome?: string; docs?: DocRef[];
  subject?: string; marker?: string;
}
interface CountItem { key: string; n: number }
interface SollSchritt {
  schritt: string; processing_path?: string; doc_types?: string[];
  akteur_von_an?: string; baustein_refs?: string[]; luecke?: string; evidenz?: string;
}
interface Familie {
  id: string; name: string; threads: number; tasks: number;
  saved_hours_6mo: number; chase_quote: number; triage: string;
  set?: string; als_karte?: boolean;
  kernablauf?: string[]; checklist_top?: ChecklistItem[]; bausteine?: string[];
  beispiele?: FamilieBeispiel[];
  roles?: CountItem[]; outcomes?: Record<string, number>;
  trigger_classes?: CountItem[]; processing_paths?: CountItem[];
  triage_begruendung?: string; soll_prozess?: SollSchritt[];
}
interface AgentStufe {
  stufe: number; name: string; status: string; cluster: number; hours: number;
  by_dept?: Record<string, number>;
}
interface RecipeSketch {
  trigger?: string; steps?: string[]; end_states?: string[];
  // Deutsche Felder (separates JSON-Update) — bevorzugt, sonst engl. Fallback.
  trigger_de?: string; steps_de?: string[]; endstates_de?: string[];
}
interface AgentCluster {
  id: string; name: string; name_de?: string; dept: string; freq?: number;
  saved_hours_6mo?: number; stage: number;
  required_capabilities: string[]; rationale?: string;
  recipe_sketch?: RecipeSketch; warum_autonom?: string; warum_autonom_de?: string;
  stage1_scope?: string; stage1_scope_de?: string;
  teil2_recipe_vorziehbar?: string; teil2_recipe_vorziehbar_de?: string;
  reassign_suggestion?: string; reassign_suggestion_de?: string;
  urteil_bleibt_human?: string; urteil_bleibt_human_de?: string; heutiger_ablauf?: string;
  rationale_de?: string;
  begruendung_ausfuehrlich?: string;
}
interface VorfallSubject { date: string; subject: string; thread_id?: string; sf_url?: string; docs?: DocRef[] }
interface FamilienRef { familie: string; overlap: number }
interface Vorfall {
  vorfall_id: string; thread_count: number; task_count?: number; email_count?: number;
  depts: string[]; accounts: string[]; match_terms: string[];
  zeitspanne?: { start?: string; end?: string; days?: number };
  thread_subjects: VorfallSubject[];
  zweck_1zeile?: string; familien_refs?: FamilienRef[];
}
interface DocTyp {
  typ: string; volumen_6m: number; volumen_quelle: string;
  verarbeitungspfad: string; verarbeitungspfad_begruendung?: string;
  zustaendige_karten?: string[]; familien?: string[]; sf_bestand?: string;
  beispiel_docs?: DocRef[];
}
interface VorfallKetten {
  kpis: {
    n_vorfaelle: number; n_multi_thread: number; threads_in_multi: number;
    share_threads_in_multi_pct: number; total_threads_with_links: number;
    largest_vorfall_threads: number;
  };
  top_vorfaelle: Vorfall[];
}
interface Board {
  meta?: Record<string, unknown>;
  kpis: Kpis;
  prerequisites: Prereq[];
  quick_wins: QuickWin[];
  familien: Familie[];
  familien_status?: string;
  doc_typ_map?: DocTyp[];
  agent_roadmap: {
    stufen: AgentStufe[]; clusters?: AgentCluster[];
    top_recipes?: unknown[]; reassigns?: unknown[]; kernbefund?: string;
  };
  vorfall_ketten?: VorfallKetten;
}

// ── Fetch-Hook (ein Request, im State gehalten; reload fuer Retry-Zustand) ────
let _cache: Board | null = null;
export function useDecisionBoard(active: boolean): { data: Board | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<Board | null>(_cache);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    api.get<Board>("/email-automation/decision-board")
      .then((d) => { _cache = d; setData(d); setError(null); })
      .catch((e) => setError((e as Error).message));
  }, []);
  useEffect(() => {
    if (!active || _cache) return;
    load();
  }, [active, load]);
  const reload = useCallback(() => { _cache = null; setData(null); setError(null); load(); }, [load]);
  return { data, error, reload };
}

// ── Zustands-Bausteine (Skeleton statt Spinner · Fehler mit Retry) — Befund 8 ─
export function BoardSkeleton() {
  return (
    <div className="space-y-3 animate-pulse" aria-busy="true" aria-label="Inhalt lädt">
      <div className="h-16 rounded-lg bg-surface-3/60" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2.5">
            <div className="h-5 w-2/3 rounded bg-surface-3/60" />
            {[0, 1, 2].map((j) => <div key={j} className="h-20 rounded-lg bg-surface-3/50" />)}
          </div>
        ))}
      </div>
    </div>
  );
}
export function BoardError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <Card padding="none" className="p-5 max-w-lg">
      <div className="text-sm font-semibold text-danger mb-1">Konnte die Daten nicht laden</div>
      <div className="text-[0.82rem] text-text-3 mb-3 break-words">{error}</div>
      <button onClick={onRetry} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-500 text-white text-[0.82rem] font-medium hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface">
        <RotateCcw className="w-3.5 h-3.5" /> Erneut versuchen
      </button>
    </Card>
  );
}

// ── Entscheidungs-Persistenz (P0 Cockpit) ────────────────────────────────────
// Entscheidungen leben serverseitig (Tabelle email_automation_decision), NICHT im
// kuratierten Board-JSON (das ist Generator-Output). Verknuepft ueber card_id.
export interface Decision {
  id: string;
  card_id: string;
  card_kind: string | null;
  card_title: string | null;
  question: string | null;
  chosen_option: string | null;
  rationale: string | null;
  decided_by: string;
  decided_by_email: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_department: string | null;
  task_id: string | null;
  status: string;
  created_at: string;
}

// Modul-Cache: ein Fetch, geteilt von Landing-Rollup UND L2-DecisionBlock.
let _decisions: Decision[] | null = null;
export function useDecisions(active: boolean): {
  decisions: Decision[];
  loaded: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [decisions, setDecisions] = useState<Decision[]>(_decisions ?? []);
  const [loaded, setLoaded] = useState<boolean>(_decisions !== null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await api.get<Decision[]>("/email-automation/decisions");
      _decisions = d;
      setDecisions(d);
      setLoaded(true);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    if (_decisions === null) void reload();
    else { setDecisions(_decisions); setLoaded(true); }
  }, [active, reload]);

  return { decisions, loaded, error, reload };
}

// L1-Href eines Segments unter Erhalt der aktuellen Filter (dept/lever) — fuer
// klickbare Breadcrumbs + stabile Back-Ziele auf den Detailseiten.
function segHref(params: URLSearchParams, seg: string): string {
  const u = new URLSearchParams(params);
  u.set("seg", seg);
  return `/email-automation?${u.toString()}`;
}

function fmtDateShort(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("de-DE");
}

// ── Umsetzungs-Status als erste Klasse (P1.1) ────────────────────────────────
// Editierbarer Status je Bau-Einheit, serverseitig persistiert (Tabelle
// email_automation_card_status, NICHT im Board-JSON). Der EFFEKTIVE Status einer
// Karte: gesetzte Status-Zeile → sonst 'entschieden' falls Entscheidung existiert
// → sonst 'offen'. Rollup UND die Status-Badges spiegeln DIESEN Status.
export type CardStatus =
  | "offen" | "entschieden" | "in_bau" | "live" | "wirkung_gemessen";
export const STATUS_STEPS: CardStatus[] = [
  "offen", "entschieden", "in_bau", "live", "wirkung_gemessen",
];
export const STATUS_LABEL: Record<CardStatus, string> = {
  offen: "offen",
  entschieden: "entschieden",
  in_bau: "in Bau",
  live: "live",
  wirkung_gemessen: "Wirkung gemessen",
};
// Pill-Stile (bestehende Semantik-Tokens, dark-aware) — Fortschritts-Progression
// grau → brand → amber → grün → grün-solid.
const STATUS_PILL: Record<CardStatus, string> = {
  offen: "bg-surface-3 text-text-2",
  entschieden: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300",
  in_bau: "bg-warning/10 text-warning",
  live: "bg-success/10 text-success",
  wirkung_gemessen: "bg-success text-white",
};

export interface CardStatusRow {
  id: string;
  card_id: string;
  card_kind: string | null;
  card_title: string | null;
  status: CardStatus;
  note: string | null;
  updated_by: string | null;
  updated_at: string;
}

let _cardStatus: CardStatusRow[] | null = null;
export function useCardStatuses(active: boolean): {
  statuses: CardStatusRow[];
  loaded: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [statuses, setStatuses] = useState<CardStatusRow[]>(_cardStatus ?? []);
  const [loaded, setLoaded] = useState<boolean>(_cardStatus !== null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await api.get<CardStatusRow[]>("/email-automation/card-status");
      _cardStatus = d;
      setStatuses(d);
      setLoaded(true);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    if (_cardStatus === null) void reload();
    else { setStatuses(_cardStatus); setLoaded(true); }
  }, [active, reload]);

  return { statuses, loaded, error, reload };
}

/** Map card_id → gesetzter Status (nur die serverseitig gespeicherten Zeilen). */
function statusMapOf(rows: CardStatusRow[]): Map<string, CardStatus> {
  return new Map(rows.map((r) => [r.card_id, r.status] as const));
}
/** Effektiver Status: gesetzte Zeile → 'entschieden' falls entschieden → 'offen'. */
function effStatus(
  cardId: string,
  set: Map<string, CardStatus>,
  decidedCardIds: Set<string>,
): CardStatus {
  const s = set.get(cardId);
  if (s) return s;
  if (decidedCardIds.has(cardId)) return "entschieden";
  return "offen";
}

/** Kleiner Status-Pill (spiegelt den effektiven Umsetzungs-Status). */
function StatusBadge({ status }: { status: CardStatus }) {
  return (
    <span className={clsx(
      "inline-flex items-center px-2 py-0.5 rounded-md text-[0.7rem] font-semibold whitespace-nowrap",
      STATUS_PILL[status],
    )}>{STATUS_LABEL[status]}</span>
  );
}

/** Umsetzungs-Status-Editor (P1.1): setzt den editierbaren Status der Karte
 *  serverseitig (PUT /card-status/{id}). Segmentierte Leiste offen → entschieden
 *  → in Bau → live → Wirkung; aktueller Status hervorgehoben. Verwendet die
 *  bestehende Pill-Semantik (StatusBadge/STATUS_PILL). */
function StatusEditor({ card, current, updatedBy, updatedAt, onChanged }: {
  card: { id: string; kind: string; title: string };
  current: CardStatus;
  updatedBy?: string | null;
  updatedAt?: string | null;
  onChanged: () => Promise<void> | void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState<CardStatus | null>(null);

  async function setStatus(s: CardStatus) {
    if (s === current || saving) return;
    setSaving(s);
    try {
      await api.put(
        `/email-automation/card-status/${encodeURIComponent(card.id)}`,
        { status: s, card_kind: card.kind, card_title: card.title },
      );
      await onChanged();
      toast.ok("Status gesetzt", `${card.title} → ${STATUS_LABEL[s]}`);
    } catch (e) {
      toast.err("Status nicht gesetzt", (e as Error).message);
    } finally {
      setSaving(null);
    }
  }

  return (
    <Card padding="none" className="p-3.5 mb-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-3">Umsetzungs-Status</div>
        {updatedBy && updatedAt && (
          <span className="text-[0.7rem] text-text-3 normal-case">· zuletzt {updatedBy} · {fmtDateShort(updatedAt)}</span>
        )}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {STATUS_STEPS.map((s, i) => (
          <span key={s} className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setStatus(s)}
              disabled={saving !== null}
              aria-pressed={s === current}
              className={clsx(
                "px-2.5 py-1 rounded-md text-[0.78rem] font-semibold whitespace-nowrap border transition-colors disabled:opacity-60",
                s === current
                  ? clsx(STATUS_PILL[s], "border-transparent ring-1 ring-inset ring-black/10")
                  : "bg-surface border-border text-text-2 hover:border-brand-500",
              )}
              title={s === current ? "aktueller Status" : `Status auf „${STATUS_LABEL[s]}" setzen`}
            >{saving === s ? "…" : STATUS_LABEL[s]}</button>
            {i < STATUS_STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-text-3 shrink-0" />}
          </span>
        ))}
      </div>
    </Card>
  );
}

/** Entscheiden-&-zuweisen-Block: zeigt bereits erfasste Entscheidungen einer
 *  Karte (persistiert, uebersteht Reload) und oeffnet den Erfass-Dialog. Beim
 *  Speichern entsteht ein echter Atlas-Task (owner = Picker-Person). Zeigt oben
 *  den editierbaren Umsetzungs-Status (P1.1). Genutzt in Quick-Win-, Prereq- UND
 *  Familien-L2. */
export function DecisionBlock({ card, questions, primaryCta = true }: {
  card: { id: string; kind: "quick_win" | "prereq" | "familie"; title: string };
  questions?: { frage: string; optionen?: string[] }[];
  // primaryCta=true → oranger Aktions-Akzent (der EINE Primaer-CTA der Ansicht).
  // In Kontexten mit anderem Orange-CTA (Task-L3-Kopf) auf false → neutrales Brand.
  primaryCta?: boolean;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { decisions, reload } = useDecisions(true);
  const { statuses, reload: reloadStatus } = useCardStatuses(true);
  const mine = decisions.filter((d) => d.card_id === card.id); // Server: neueste zuerst
  const statusRow = statuses.find((s) => s.card_id === card.id);
  const currentStatus: CardStatus =
    statusRow?.status ?? (mine.length > 0 ? "entschieden" : "offen");

  const openQuestions = (questions || []).filter((q) => q.frage);
  const [open, setOpen] = useState(false);

  // Formular-State
  const [qIdx, setQIdx] = useState(0);
  const [option, setOption] = useState("");
  const [rationale, setRationale] = useState("");
  const [owner, setOwner] = useState<Person[]>([]);
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);

  // Ziel-Termin deutsch (TT.MM.JJJJ); `due` bleibt der ISO-Kalendertag, den
  // `submit()` unverändert als due_date schickt. Unlesbar → Speichern gesperrt.
  const dueField = useGermanDateField(due, setDue);

  const activeQ = openQuestions[qIdx];
  const optionChoices =
    activeQ?.optionen && activeQ.optionen.length
      ? activeQ.optionen
      : ["Bauen beauftragen", "Zurückstellen"];

  function openModal() {
    setQIdx(0); setOption(""); setRationale(""); setOwner([]); setDue("");
    setOpen(true);
  }

  async function submit() {
    if (saving || dueField.invalid) return;
    setSaving(true);
    try {
      await api.post("/email-automation/decisions", {
        card_id: card.id,
        card_kind: card.kind,
        card_title: card.title,
        question: activeQ?.frage ?? null,
        chosen_option: option.trim() || null,
        rationale: rationale.trim() || null,
        assignee: owner[0]?.email ?? null,
        due_date: due || null,
      });
      await reload();
      toast.ok(
        "Entscheidung erfasst",
        owner[0]?.name
          ? `Atlas-Task angelegt · Owner ${owner[0].name}`
          : "Atlas-Task angelegt (dir selbst zugewiesen)",
      );
      setOpen(false);
    } catch (e) {
      toast.err("Konnte nicht speichern", (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-5">
      {/* Umsetzungs-Status als erste Klasse (P1.1) — editierbar, persistiert */}
      <StatusEditor
        card={card}
        current={currentStatus}
        updatedBy={statusRow?.updated_by}
        updatedAt={statusRow?.updated_at}
        onChanged={reloadStatus}
      />

      <div className="mb-2 flex items-center gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-3">Entscheiden &amp; zuweisen</div>
        {mine.length > 0 && (
          <span className="rounded-full bg-success/10 text-success px-2 py-0.5 text-[0.7rem] font-semibold normal-case tracking-normal">{mine.length} erfasst</span>
        )}
        <button
          onClick={openModal}
          className={clsx(
            "ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-[0.82rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
            primaryCta
              ? "bg-action hover:brightness-95 focus-visible:ring-action"
              : "bg-brand-500 hover:bg-brand-600 focus-visible:ring-brand-500",
          )}
        >
          <UserPlus className="w-3.5 h-3.5" /> Entscheiden &amp; zuweisen
        </button>
      </div>

      {/* Offene Fragen (Kontext, aus dem kuratierten Board) */}
      {openQuestions.length > 0 && (
        <Card padding="none" className="p-3 mb-2">
          <div className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-1.5">Offene Fragen ({openQuestions.length})</div>
          <ol className="space-y-1 list-decimal pl-5 text-[0.84rem] text-text-2">
            {openQuestions.map((q, i) => <li key={i}>{q.frage}</li>)}
          </ol>
        </Card>
      )}

      {/* Erfasste Entscheidungen (persistiert — uebersteht Reload) */}
      {mine.length > 0 ? (
        <Card padding="none" className="overflow-hidden">
          {mine.map((d) => (
            <div key={d.id} className="flex flex-col sm:flex-row sm:items-start gap-2 px-3.5 py-2.5 border-t border-border first:border-t-0">
              <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <div className="text-[0.86rem] text-text">
                  <b className="font-semibold">{d.chosen_option || "Entschieden"}</b>
                  {d.question && <span className="text-text-3"> · {d.question}</span>}
                </div>
                {d.rationale && <div className="text-[0.78rem] text-text-3 leading-snug">{d.rationale}</div>}
                <div className="text-[0.72rem] text-text-3 mt-0.5">
                  {d.owner_name && (
                    <>Owner <b className="text-text-2">{d.owner_name}</b>{d.owner_department ? ` (${d.owner_department})` : ""} · </>
                  )}
                  entschieden von {d.decided_by} · {fmtDateShort(d.created_at)}
                </div>
              </div>
              {d.task_id && (
                <button
                  onClick={() => navigate("/tasks")}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.7rem] font-semibold hover:bg-brand-500 hover:text-white transition-colors"
                  title="Zugehoerigen Atlas-Task im Aufgaben-Tab oeffnen"
                >
                  Task ↗
                </button>
              )}
            </div>
          ))}
        </Card>
      ) : (
        <div className="text-[0.8rem] text-text-3">Noch keine Entscheidung erfasst. „Entscheiden &amp; zuweisen" legt einen echten Atlas-Task mit Owner an und haelt Option + Begruendung fest.</div>
      )}

      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          size="md"
          icon={<UserPlus className="w-4 h-4" />}
          title="Entscheiden & zuweisen"
          description={card.title}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-3 text-text">Abbrechen</button>
              <button onClick={submit} disabled={saving || dueField.invalid} className="px-3 py-1.5 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50">
                {saving ? "Speichere …" : "Speichern + Task anlegen"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {openQuestions.length > 1 && (
              <Field label="Frage">
                <Select value={qIdx} onChange={(e) => { setQIdx(Number(e.target.value)); setOption(""); }}>
                  {openQuestions.map((q, i) => <option key={i} value={i}>{q.frage}</option>)}
                </Select>
              </Field>
            )}
            {openQuestions.length === 1 && (
              <div className="text-[0.82rem] text-text-2"><b className="text-text">Frage:</b> {openQuestions[0].frage}</div>
            )}
            <Field label="Entscheidung">
              <div className="flex gap-1.5 flex-wrap mb-1.5">
                {optionChoices.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setOption(opt)}
                    className={clsx(
                      "px-2.5 py-1 rounded-md border text-[0.8rem]",
                      option === opt ? "bg-brand-500 border-brand-500 text-white" : "border-border-strong text-text-2 bg-surface hover:border-brand-500",
                    )}
                  >{opt}</button>
                ))}
              </div>
              <Input value={option} onChange={(e) => setOption(e.target.value)} placeholder="oder frei formulieren …" />
            </Field>
            <Field label="Begründung" optional>
              <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Warum diese Entscheidung? (wird am Task + in der Historie festgehalten)" />
            </Field>
            <Field label="Owner (Verantwortliche/r)" hint="Person aus dem Mitarbeiter-Verzeichnis. Leer = dir selbst zuweisen.">
              <UserPicker selected={owner} onChange={setOwner} multiple={false} placeholder="Owner suchen (Name/Email) …" />
            </Field>
            <Field
              label="Ziel-Termin"
              optional
              hint="Setzt Fälligkeit + einen Reminder am Zieltag."
              error={dueField.invalid ? DE_DATE_HINT : undefined}
            >
              <Input {...dueField.inputProps} aria-label="Ziel-Termin (TT.MM.JJJJ)" />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Badges / Pills (feste Semantik-Tokens, keine Hexfarben) ──────────────────
export function fmtHours(n: number | null | undefined): string {
  if (n == null) return "—";
  return `${n.toLocaleString("de-DE", { maximumFractionDigits: 1 })} h`;
}
export function fmtInt(n: number | null | undefined): string {
  if (n == null) return "—";
  return Math.round(n).toLocaleString("de-DE");
}

// Plattform-/Ziel-Badge (bES email-service, bAT Atlas, bPO Portal, bSF SF-Flow).
const PLAT_BADGE: Record<string, string> = {
  "email-service": "bg-success/10 text-success",
  atlas: "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300",
  portal: "bg-warning/10 text-warning",
  "SF-Flow": "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300",
  "Email-Service+Atlas": "bg-success/10 text-success",
};
function PlatBadge({ platform, label }: { platform: string; label?: string }) {
  const cls = PLAT_BADGE[platform] ?? "bg-surface-3 text-text-2";
  return <span className={clsx("inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap justify-self-start", cls)}>{label ?? platform}</span>;
}
// Effort-Pill (S/M/L).
function EffortPill({ effort }: { effort: string }) {
  return <span className="inline-flex items-center justify-center px-1.5 rounded-full border border-border-strong text-[0.68rem] font-semibold text-text-2 whitespace-nowrap">{effort}</span>;
}
// Blocking-Badge (hard=danger, soft=warning, config=success).
function BlockBadge({ blocking }: { blocking: string }) {
  const b = blocking.toLowerCase();
  const cls = b === "hard" ? "bg-danger/10 text-danger border border-danger/25"
    : b === "config" ? "bg-success/10 text-success"
      : "bg-warning/10 text-warning border border-warning/25";
  const label = b === "config" ? "config" : b;
  return <span className={clsx("inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap justify-self-start", cls)}>{label}</span>;
}
// Rechts-Badge einer Quick-Win-Zeile: N offen / 0 offen (ok) / geblockt (PR-Liste).
function OpenBadge({ n }: { n: number }) {
  if (n <= 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap bg-success/10 text-success justify-self-end">0 offen</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap bg-warning/10 text-warning border border-warning/25 justify-self-end">{n} offen</span>;
}
function BlockedBadge({ prs }: { prs: string[] }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap bg-danger/10 text-danger border border-danger/25 justify-self-end">{prs.join(" · ")}</span>;
}
// Ampel-Dot (Risiko-Höhe): grün=niedrig, gelb=mittel, rot=hoch.
function Dot({ tone }: { tone: "gn" | "am" | "rd" }) {
  const cls = tone === "gn" ? "bg-success" : tone === "am" ? "bg-warning" : "bg-danger";
  return <span className={clsx("w-2.5 h-2.5 rounded-full shrink-0", cls)} />;
}

// Verarbeitungspfad-Pill (processing_path-Enum, deutsch gelabelt).
const PATH_LABEL: Record<string, string> = {
  create_document: "Dokument erzeugen",
  confirm: "Bestätigen",
  negotiate: "Aushandeln",
  reconcile: "Abgleichen",
  forward_only: "Weiterleiten",
  extract_and_enter: "Extrahieren → System",
  extract_and_reconcile: "Extrahieren + Abgleich",
  chase_and_collect: "Nachfassen + Einsammeln",
  review_and_approve: "Review + Freigabe",
  sanity_check: "Sanity-Check",
  sign: "Signieren",
};
function PfadPill({ pfad }: { pfad?: string }) {
  if (!pfad) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-border-strong text-[0.66rem] font-medium text-text-2 whitespace-nowrap" title={pfad}>
      {PATH_LABEL[pfad] || pfad}
    </span>
  );
}

/** Baustein-Referenz: klickbarer Chip, wenn die ID eine Board-Karte (Quick-Win/PR)
 *  ist; sonst neutraler Chip (sf_coverage_map-Key ohne eigenes L2). */
function BausteinChip({ id, board }: { id: string; board: Board }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resolvable =
    board.quick_wins.some((q) => q.id === id) ||
    board.prerequisites.some((p) => p.id === id);
  if (!resolvable) {
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface-3 text-text-3 text-[0.68rem] font-medium whitespace-nowrap" title="kein eigenes Karten-L2 (Coverage-Key)">{id}</span>;
  }
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/email-automation/decide/${id}${params.toString() ? `?${params.toString()}` : ""}`); }}
      className="inline-flex items-center px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.68rem] font-semibold whitespace-nowrap hover:bg-brand-500 hover:text-white transition-colors"
    >{id}</button>
  );
}

// ── SF-Durchgriff: externer Link auf den Salesforce-Record (öffnet neuen Tab) ─
function docTypeShort(typ?: string): string {
  if (!typ) return "";
  const m: Record<string, string> = {
    "COA Certificate of Analysis": "CoA", "Batch Documentation": "Batch-Doku",
    "Delivery Note": "Lieferschein", "Purchase Order": "PO",
    "Order Confirmation": "AB", "Customer Order": "Kunden-PO",
  };
  return m[typ] || typ;
}
// Externer SF-Link mit ↗-Kennzeichnung (thread/record). target=_blank, stopPropagation.
function SfLink({ url, label, title }: { url: string; label: string; title?: string }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      title={title || "öffnet Salesforce in neuem Tab"}
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.68rem] font-semibold whitespace-nowrap hover:bg-brand-500 hover:text-white transition-colors">
      {label}<ExternalLink className="w-2.5 h-2.5" />
    </a>
  );
}
// Doc-Chip: gekürzter Name + Typ, klickbar -> SF-Record.
function DocChip({ doc }: { doc: DocRef }) {
  const short = doc.name.length > 32 ? doc.name.slice(0, 30) + "…" : doc.name;
  return (
    <a href={doc.sf_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      title={`${doc.name}${doc.typ ? ` · ${doc.typ}` : ""} — öffnet Salesforce`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border-strong bg-surface text-[0.66rem] text-text-2 whitespace-nowrap hover:border-brand-500 hover:text-brand-600 transition-colors">
      {doc.typ && <span className="font-semibold text-text-3">{docTypeShort(doc.typ)}</span>}
      <span className="truncate max-w-[180px]">{short}</span>
      <ExternalLink className="w-2.5 h-2.5 shrink-0" />
    </a>
  );
}

// ── Volumen-Hint je Quick-Win (aus volumen.* + luecke) ───────────────────────
function volHint(qw: QuickWin): string {
  const v = qw.volumen || {};
  const bits: string[] = [];
  if (v.threads_6mo != null) bits.push(`${fmtInt(v.threads_6mo as number)} Threads/6M`);
  if (v.saved_hours_6mo != null) bits.push(`${fmtHours(v.saved_hours_6mo as number)}/6M`);
  return bits.join(" · ");
}
function daHint(qw: QuickWin): { da?: string; fehlt?: string } {
  const da = qw.bestand?.[0]?.was;
  const fehlt = qw.luecke?.[0]?.was;
  return { da, fehlt };
}
// Risiko-Ampel je Quick-Win heuristisch (nur für den Dot; Inhalt bleibt Text).
function riskTone(qw: QuickWin): "gn" | "am" | "rd" {
  const r = (qw.risiko || "").toLowerCase();
  if (r.includes("silent") || r.includes("still") || r.includes("ohne menschlich") || r.includes("keine api")) return "rd";
  if (qw.status === "blocked") return "rd";
  if (r) return "am";
  return "gn";
}

// ── ROI (saved_hours_6mo ÷ Aufwand) + Steuerungs-Rollup ──────────────────────
const EFFORT_WEIGHT: Record<string, number> = { S: 1, M: 2, L: 3 };
function roiScore(qw: QuickWin): number {
  const h = (qw.volumen?.saved_hours_6mo as number | undefined) ?? 0;
  const w = EFFORT_WEIGHT[(qw.effort_gesamt || "M").toUpperCase()] ?? 2;
  return h / w;
}
const byRoiDesc = (a: QuickWin, b: QuickWin) => roiScore(b) - roiScore(a);

function Stat({ value, label, tone }: { value: number; label: string; tone?: "warning" | "accent" | "success" | "brand" }) {
  const color = tone === "warning" ? "text-warning" : tone === "success" ? "text-success" : tone === "brand" ? "text-brand-500" : tone === "accent" ? "text-accent-600 dark:text-accent-300" : "text-text";
  return (
    <div>
      <div className={clsx("ea-num text-xl font-semibold tabular-nums", color)}>{value}</div>
      <div className="text-xs text-text-3">{label}</div>
    </div>
  );
}

/** Steuerungs-Rollup: Umsetzungs-Status-Pipeline (offen entscheidbar · entschieden
 *  · in Bau · live · Wirkung) · naechster Blocker (PR) · Gesamtpotenzial ·
 *  Owner-Verteilung. Die Zaehler „in Bau"/„live"/„Wirkung" zaehlen den ECHTEN,
 *  editierbaren Umsetzungs-Status (P1.1: statusByCard → sonst 'entschieden' falls
 *  entschieden → sonst 'offen'), nicht mehr nur „entschieden = zugewiesen". */
function SteuerRollup({ board, decisions, statusByCard, entscheidbar, onOpen }: {
  board: Board;
  decisions: Decision[];
  statusByCard: Map<string, CardStatus>;
  entscheidbar: QuickWin[];
  onOpen: (cardId: string) => void;
}) {
  const yearHours = Math.round((board.kpis.total_saved_hours_6mo || 0) * 2);
  const decidedCardIds = new Set(decisions.map((d) => d.card_id));
  // Alle Karten mit einem Signal (entschieden ODER Status gesetzt).
  const trackedIds = new Set<string>([...decidedCardIds, ...statusByCard.keys()]);
  const eff = (id: string) => effStatus(id, statusByCard, decidedCardIds);
  const countStatus = (st: CardStatus) =>
    Array.from(trackedIds).filter((id) => eff(id) === st).length;
  // Offene Entscheidungen = jetzt-entscheidbare Karten, deren effektiver Status
  // noch 'offen' ist (weder entschieden noch weitergeschoben).
  const offeneEntscheidungen = entscheidbar.filter((q) => eff(q.id) === "offen").length;
  const entschieden = countStatus("entschieden");
  const inBau = countStatus("in_bau");
  const live = countStatus("live");
  const wirkung = countStatus("wirkung_gemessen");

  const nextBlocker = [...board.prerequisites]
    .filter((p) => (p.blocking || "").toLowerCase() === "hard" && p.status !== "done")
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))[0];
  // Owner-Verteilung: juengste Entscheidung je Karte (decisions sind neueste zuerst).
  const seen = new Set<string>();
  const dist = new Map<string, number>();
  decisions.forEach((d) => {
    if (seen.has(d.card_id)) return;
    seen.add(d.card_id);
    const k = d.owner_department || d.owner_name || "—";
    dist.set(k, (dist.get(k) || 0) + 1);
  });

  return (
    <Card padding="none" className="mb-4 p-4">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-3">Steuerungs-Übersicht</div>
      {/* Umsetzungs-Status-Pipeline — echte Zaehler aus dem editierbaren Status */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-3">
        <Stat value={offeneEntscheidungen} label="offene Entscheidungen" tone="warning" />
        <Stat value={entschieden} label="entschieden" tone="accent" />
        <Stat value={inBau} label="in Bau" tone="brand" />
        <Stat value={live} label="live" tone="success" />
        <Stat value={wirkung} label="Wirkung gemessen" tone="success" />
      </div>
      {/* Nächster Blocker + Gesamtpotenzial */}
      <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 items-start">
        <div className="min-w-0">
          <div className="text-xs text-text-3 mb-0.5">Nächster Blocker</div>
          {nextBlocker ? (
            <button
              onClick={() => onOpen(nextBlocker.id)}
              className="flex items-center gap-1 text-left text-sm font-semibold text-danger hover:underline min-w-0 max-w-full"
              title={nextBlocker.titel}
            >
              <span className="shrink-0">{nextBlocker.id}</span>
              <span className="text-text-2 font-normal truncate">· {nextBlocker.titel}</span>
            </button>
          ) : (
            <div className="text-sm text-text-2">keiner</div>
          )}
        </div>
        <div className="md:text-right">
          <div className="ea-num text-xl font-semibold tabular-nums text-brand-500">≈ {fmtInt(yearHours)} h</div>
          <div className="text-xs text-text-3">Gesamtpotenzial / Jahr</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-text-3 mr-1">Owner-Verteilung nach Abteilung:</span>
        {dist.size === 0 ? (
          <span className="text-xs text-text-3">noch keine Zuweisungen</span>
        ) : (
          Array.from(dist.entries()).map(([k, n]) => (
            <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-3 text-text-2 text-xs">
              {k} <b className="text-text tabular-nums">{n}</b>
            </span>
          ))
        )}
      </div>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// View: Entscheiden
// ═════════════════════════════════════════════════════════════════════════════
export function DecideView({ board }: { board: Board }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qs = params.toString();
  const open = (cardId: string) => navigate(`/email-automation/decide/${cardId}${qs ? `?${qs}` : ""}`);

  const { decisions } = useDecisions(true);
  const { statuses } = useCardStatuses(true);
  const statusByCard = statusMapOf(statuses);
  const decidedCardIds = new Set(decisions.map((d) => d.card_id));
  const rowStatus = (id: string) => effStatus(id, statusByCard, decidedCardIds);
  const qw = board.quick_wins;
  // Gruppen nach ROI sortiert (saved_hours_6mo ÷ Aufwand) — beantwortet "womit anfangen".
  const entscheidbar = qw.filter((q) => q.status === "entscheidbar").sort(byRoiDesc);
  const vorbereitet = qw.filter((q) => q.status === "vorbereitet").sort(byRoiDesc);
  const blocked = qw.filter((q) => q.status === "blocked").sort(byRoiDesc);
  const offenCount = (q: QuickWin) => (q.entscheidungen_offen || []).filter((e) => e.frage).length;
  const totalOffen = entscheidbar.reduce((s, q) => s + offenCount(q), 0);

  return (
    <>
      {/* Steuerungs-Rollup — Status-Pipeline spiegelt den echten Umsetzungs-Status */}
      <SteuerRollup board={board} decisions={decisions} statusByCard={statusByCard} entscheidbar={entscheidbar} onOpen={open} />

      {/* Voraussetzungen (Prerequisites) */}
      <GroupHead label="Voraussetzungen — vor der Klammer" count={board.prerequisites.length} note="schalten Karten & Agent-Recipes frei" />
      <Card padding="none" className="overflow-hidden">
        {board.prerequisites.map((pr, i) => (
          <PrereqRow key={pr.id} pr={pr} first={i === 0} status={rowStatus(pr.id)} onClick={() => open(pr.id)} />
        ))}
      </Card>

      {/* Jetzt entscheidbar */}
      <GroupHead label="Jetzt entscheidbar" count={entscheidbar.length} note={`${totalOffen} offene Fragen`} tone="success" />
      <Card padding="none" className="overflow-hidden">
        {entscheidbar.map((q) => (
          <QwRow key={q.id} qw={q} status={rowStatus(q.id)} right={<OpenBadge n={offenCount(q)} />} onClick={() => open(q.id)} />
        ))}
      </Card>

      {/* Vorbereitet */}
      <GroupHead label="Vorbereitet — wartet auf Deine Antworten" count={vorbereitet.length} tone="accent" />
      <Card padding="none" className="overflow-hidden">
        {vorbereitet.map((q) => (
          <QwRow key={q.id} qw={q} status={rowStatus(q.id)} right={<OpenBadge n={offenCount(q)} />} onClick={() => open(q.id)} />
        ))}
      </Card>

      {/* Geblockt */}
      <GroupHead label="Geblockt durch Voraussetzungen" count={blocked.length} tone="danger" />
      <Card padding="none" className="overflow-hidden">
        {blocked.map((q) => (
          <QwRow key={q.id} qw={q} status={rowStatus(q.id)} right={<BlockedBadge prs={q.prerequisites || []} />} onClick={() => open(q.id)} />
        ))}
      </Card>

      {/* Prozess-Familien — Detailebene UNTER Entscheiden (P1.2; kein Top-Level) */}
      <FamilienSection board={board} />
    </>
  );
}

function Kpi({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <Card padding="none" className="px-4 py-3">
      <div className={clsx("ea-num text-xl font-semibold tabular-nums", accent ? "text-brand-500" : "text-text")}>{value}</div>
      <div className="mt-0.5 text-xs text-text-3">{label}</div>
    </Card>
  );
}

function GroupHead({ label, count, note, tone }: { label: string; count: number; note?: string; tone?: "success" | "danger" | "accent" }) {
  const color = tone === "success" ? "text-success" : tone === "danger" ? "text-danger" : tone === "accent" ? "text-accent-600 dark:text-accent-300" : "text-text-3";
  return (
    <div className={clsx("mt-5 mb-2 flex items-center gap-2 ea-section", color)}>
      <span>{label}</span>
      <span className="rounded-full bg-surface-3 text-text-2 px-2 py-0.5 font-medium normal-case tracking-normal">{count}</span>
      {note && <span className="ml-auto font-medium normal-case tracking-normal text-text-3">{note}</span>}
    </div>
  );
}

// Prereq-Zeile — feste Grid-Spalten: id · name · plat · effort · block · hint · chevron.
function PrereqRow({ pr, first, status, onClick }: { pr: Prereq; first: boolean; status: CardStatus; onClick: () => void }) {
  const hero = first;
  const platLabel = pr.platform === "email-service" ? "email-service" : pr.platform === "atlas" ? "Atlas" : pr.platform === "portal" ? "Portal" : pr.platform;
  return (
    <div
      onClick={onClick} {...keyActivate(onClick)} aria-label={pr.titel}
      className={clsx(
        "grid items-center gap-2.5 px-3.5 py-2.5 cursor-pointer border-t border-border first:border-t-0",
        // Feste Badge-Spalten [Hebel] [Aufwand] [Block] [Status] — untereinander.
        "grid-cols-[44px_minmax(0,1fr)] md:grid-cols-[44px_260px_108px_38px_58px_92px_minmax(0,1fr)_16px]",
        hero ? "bg-brand-50 hover:bg-brand-100 dark:bg-brand-500/10 dark:hover:bg-brand-500/15" : "hover:bg-surface-2",
      )}
    >
      <span className="font-bold text-brand-500 text-[0.82rem]">{pr.id}</span>
      <span className="text-sm font-medium text-text line-clamp-2 leading-snug">{pr.titel}</span>
      <div className="hidden md:block"><PlatBadge platform={pr.platform} label={platLabel} /></div>
      <div className="hidden md:block"><EffortPill effort={pr.effort} /></div>
      <div className="hidden md:block"><BlockBadge blocking={pr.blocking} /></div>
      <div className="hidden md:block">{status !== "offen" ? <StatusBadge status={status} /> : <span className="text-[0.7rem] text-text-3">—</span>}</div>
      <span className="hidden md:block text-[0.82rem] text-text-3 truncate">{pr.beschreibung_kurz}</span>
      <ChevronRight className="hidden md:block w-4 h-4 text-text-3" />
    </div>
  );
}

// Quick-Win-Zeile — feste Grid-Spalten: dot · name · plat · star · hint · right · chevron.
function QwRow({ qw, status, right, onClick }: { qw: QuickWin; status: CardStatus; right: ReactNode; onClick: () => void }) {
  const star = qw.status === "vorbereitet" || (qw.effort_gesamt === "S" && qw.status === "entscheidbar");
  const { da, fehlt } = daHint(qw);
  const vh = volHint(qw);
  return (
    <div
      onClick={onClick} {...keyActivate(onClick)} aria-label={qw.titel}
      className={clsx(
        "grid items-center gap-2.5 px-3.5 py-2.5 cursor-pointer border-t border-border first:border-t-0 hover:bg-surface-2",
        // Feste Badge-Spalten [Hebel] [Status] [★] — spaltenweise exakt untereinander.
        "grid-cols-[10px_minmax(0,1fr)_auto] md:grid-cols-[10px_300px_112px_96px_18px_minmax(0,1fr)_78px_16px]",
      )}
    >
      <Dot tone={riskTone(qw)} />
      <span className="text-sm font-medium text-text line-clamp-2 leading-snug">{qw.titel}</span>
      <div className="hidden md:block"><PlatBadge platform={qw.plattform} label={qw.plattform} /></div>
      <div className="hidden md:block">{status !== "offen" ? <StatusBadge status={status} /> : <span className="text-[0.7rem] text-text-3">—</span>}</div>
      <span className="hidden md:flex justify-center">{star && <Star className="w-3.5 h-3.5 text-warning fill-warning" />}</span>
      <div className="hidden md:flex items-center min-w-0">
        <span className="text-[0.82rem] text-text-3 truncate">
          {da && <><b className="text-text-2 font-medium">Da:</b> {da} </>}
          {fehlt && <><b className="text-text-2 font-medium">Fehlt:</b> {fehlt} </>}
          {vh && <span className="text-text-3">· {vh}</span>}
        </span>
      </div>
      <div className="justify-self-end">{right}</div>
      <ChevronRight className="hidden md:block w-4 h-4 text-text-3" />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// View: Prozess-Familien
// ═════════════════════════════════════════════════════════════════════════════
const FAMILIEN_SETS: [string, string][] = [
  ["pilot", "Lieferabwicklung (SCM+Logistik) · Pilot"],
  ["ef", "EF Dokumente & Review"],
  ["order_entry", "Order Entry"],
];

/** Prozess-Familien als DETAILEBENE unter Entscheiden (P1.2) — kein eigenes
 *  Top-Level-Segment mehr. Karten fuehren in die Familien-L2 (FamDetail), die den
 *  Soll-Prozess, Beispiel-Threads (SF-Durchgriff) UND den Vorfall-Ketten-Beleg-
 *  Block traegt. Der dekorative Stundenbalken auf den Karten entfaellt. */
function FamilienSection({ board }: { board: Board }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qs = params.toString();
  const totalThreads = board.familien.reduce((s, f) => s + (f.threads || 0), 0);
  const totalHours = board.familien.reduce((s, f) => s + (f.saved_hours_6mo || 0), 0);
  const buildable = board.familien.filter((f) => f.als_karte && f.triage !== "human_only").length;

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-text">
        <span className="uppercase tracking-wide text-xs text-text-3">Prozess-Familien</span>
        <span className="rounded-full bg-surface-3 text-text-2 px-2 py-0.5 text-xs font-medium">{board.familien.length}</span>
        <span className="ml-auto text-xs font-normal text-text-3">Soll-Prozess · Beispiel-Threads · Vorfall-Belege je Familie</span>
      </div>
      <div className="mb-3 rounded-lg border border-border bg-surface-2 px-3.5 py-2.5 text-[0.85rem] text-text-2">
        <b className="text-text">Aufwand ≠ Automatisierbarkeit:</b> Die größte Familie eines Sets bleibt oft menschlich — der baubare Hebel liegt in den Architektur-Ketten und Reminder-Brücken.
      </div>

      {/* KPI-Leiste — Gesamtsummen über alle drei Sets */}
      <div className="mb-2 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi value="3 Sets" label="Familien-Sets extrahiert" />
        <Kpi value={fmtInt(totalThreads)} label="Threads gesamt" />
        <Kpi value={fmtHours(totalHours)} label="pro 6 Monate (alle Familien)" accent />
        <Kpi value={String(buildable)} label="baubare Familien (Kette / Brücke / Quick-Win)" />
      </div>

      {FAMILIEN_SETS.map(([setKey, setLabel]) => {
        const inSet = board.familien.filter((f) => (f.set || "pilot") === setKey);
        const cards = inSet.filter((f) => f.als_karte !== false)
          .sort((a, b) => (b.saved_hours_6mo || 0) - (a.saved_hours_6mo || 0));
        const rest = inSet.filter((f) => f.als_karte === false);
        if (inSet.length === 0) return null;
        return (
          <div key={setKey}>
            <GroupHead label={setLabel} count={cards.length} />
            <div className="grid md:grid-cols-2 gap-3">
              {cards.map((f) => (
                <FamCard key={f.id} f={f} onClick={() => navigate(`/email-automation/decide/${f.id}${qs ? `?${qs}` : ""}`)} />
              ))}
            </div>
            {rest.length > 0 && (
              <div className="mt-2 text-xs text-text-3">
                Nicht als Karte: {rest.map((f) => `${f.name} (${fmtInt(f.threads)} Threads · ${fmtHours(f.saved_hours_6mo)})`).join(" · ")}
              </div>
            )}
          </div>
        );
      })}

      {board.familien_status && (
        <div className="mt-5 rounded-lg border border-border bg-surface-2 px-3.5 py-2 text-xs text-text-2">
          <b className="text-text">Status:</b> {board.familien_status}
        </div>
      )}
    </div>
  );
}

function TriageBadge({ triage }: { triage: string }) {
  const map: Record<string, [string, string]> = {
    architecture_chain: ["Architektur-Kette", "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"],
    reminder_bridge: ["Reminder-Brücke", "bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-300"],
    standalone_quick_win: ["Quick-Win", "bg-warning/10 text-warning"],
    human_only: ["human-only", "bg-surface-3 text-text-2"],
  };
  const [label, cls] = map[triage] ?? [triage, "bg-surface-3 text-text-2"];
  return <span className={clsx("inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-semibold whitespace-nowrap", cls)}>{label}</span>;
}

function FamCard({ f, onClick }: { f: Familie; onClick: () => void }) {
  return (
    <Card padding="none" className="px-4 py-3.5 cursor-pointer hover:border-brand-500 transition-colors" onClick={onClick} {...keyActivate(onClick)} aria-label={f.name}>
      <div className="flex items-center gap-2 mb-1.5">
        <h3 className="text-[0.95rem] font-semibold text-text mr-auto">{f.name}</h3>
        <TriageBadge triage={f.triage} />
      </div>
      <div className="flex gap-5 text-xs text-text-3 mt-2">
        <span><b className="block text-[0.95rem] font-semibold text-text tabular-nums">{fmtHours(f.saved_hours_6mo)}</b>/ 6M</span>
        <span><b className="block text-[0.95rem] font-semibold text-text tabular-nums">{fmtInt(f.threads)}</b>Threads</span>
        <span><b className="block text-[0.95rem] font-semibold text-text tabular-nums">{fmtInt(f.tasks)}</b>Tasks</span>
        <span><b className="block text-[0.95rem] font-semibold text-text tabular-nums">{f.chase_quote.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %</b>Chase</span>
      </div>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// View: Agent-Roadmap
// ═════════════════════════════════════════════════════════════════════════════
export function AgentView({ board }: { board: Board }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qs = params.toString();
  const stufen = [...board.agent_roadmap.stufen].sort((a, b) => a.stufe - b.stufe);
  const totalH = stufen.reduce((s, x) => s + x.hours, 0) || 1;
  const barCls = ["bg-success", "bg-brand-500", "bg-slate-400"];
  const stageLabels = ["HEUTE LIVE", "NÄCHSTER SCHRITT", "ZIELBILD"];
  const stageColor = ["text-success", "text-accent-600 dark:text-accent-300", "text-text-3"];

  return (
    <>
      <div className="mb-4 rounded-lg border border-border bg-surface-2 px-3.5 py-2.5 text-[0.85rem] text-text-2">
        <b className="text-text">Kernbefund:</b> Experten-Reviews = deterministische Orchestrierungs-Hülle um ein menschliches Fachurteil. Die Hülle ist Stufe-2-Recipe-fähig; das Urteil bleibt beim Menschen.
      </div>

      {/* Stacked-Bar */}
      <div className="flex h-7 rounded-lg overflow-hidden border border-border mb-2">
        {stufen.map((s, i) => (
          <div key={s.stufe} className={clsx("grid place-items-center text-[0.72rem] font-semibold text-white overflow-hidden whitespace-nowrap", barCls[i] ?? "bg-slate-400")} style={{ width: `${(s.hours / totalH) * 100}%` }}>
            {(s.hours / totalH) > 0.12 ? `Stufe ${s.stufe} · ${fmtHours(s.hours)}` : `${Math.round((s.hours / totalH) * 100)}%`}
          </div>
        ))}
      </div>
      <div className="flex gap-4 flex-wrap text-xs text-text-3 mb-4">
        {stufen.map((s, i) => (
          <span key={s.stufe} className="inline-flex items-center gap-1.5">
            <span className={clsx("inline-block w-2.5 h-2.5 rounded", barCls[i] ?? "bg-slate-400")} />
            Stufe {s.stufe} · {s.cluster} Cluster
          </span>
        ))}
      </div>

      {/* Stufen-Zeilen — drillbar → L2 mit allen Clustern der Stufe */}
      <Card padding="none" className="overflow-hidden">
        {stufen.map((s, i) => (
          <div
            key={s.stufe}
            onClick={() => navigate(`/email-automation/decide/agent-stufe-${s.stufe}${qs ? `?${qs}` : ""}`)}
            {...keyActivate(() => navigate(`/email-automation/decide/agent-stufe-${s.stufe}${qs ? `?${qs}` : ""}`))}
            aria-label={`Stufe ${s.stufe} — ${s.name}`}
            className="flex flex-col sm:flex-row items-start gap-3 px-4 py-3 border-t border-border first:border-t-0 cursor-pointer hover:bg-surface-2"
          >
            <div className="w-40 shrink-0">
              <span className={clsx("text-[0.68rem] font-bold tracking-wide", stageColor[i] ?? "text-text-3")}>{stageLabels[i] ?? s.status}</span>
              <b className="block text-sm text-text">Stufe {s.stufe}</b>
            </div>
            <div className="w-40 shrink-0 text-sm text-text-2">
              <b className="text-text text-[0.95rem] tabular-nums">{fmtHours(s.hours)}</b> · {s.cluster} Cluster
            </div>
            <div className="flex-1 text-[0.85rem] text-text-2">{s.name}</div>
            <ChevronRight className="hidden sm:block w-4 h-4 text-text-3 shrink-0 self-center" />
          </div>
        ))}
      </Card>
      {board.agent_roadmap.kernbefund && (
        <p className="mt-3 text-xs text-text-3 leading-relaxed">{board.agent_roadmap.kernbefund}</p>
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// L2: Detail einer Karte (Quick-Win / Prereq / Familie)
// ═════════════════════════════════════════════════════════════════════════════
export function DecideDetailView({ cardId }: { cardId: string }) {
  const [params] = useSearchParams();
  const { data: board, error, reload } = useDecisionBoard(true);
  const backTo = `/email-automation${params.toString() ? `?${params.toString()}` : ""}`;

  // Kopfband (Titel · Segment-Tabs · Filter) liefert die persistente App-Shell;
  // Scroll + Padding ebenfalls. Hier nur Board-Guards + Karten-Dispatch.
  if (error) return <BoardError error={error} onRetry={reload} />;
  if (!board) return <BoardSkeleton />;
  const stufeMatch = /^agent-stufe-(\d)$/.exec(cardId);
  if (stufeMatch) return <AgentStageDetail board={board} stufe={Number(stufeMatch[1])} />;
  const qw = board.quick_wins.find((q) => q.id === cardId);
  if (qw) return <QwDetail qw={qw} board={board} />;
  const pr = board.prerequisites.find((p) => p.id === cardId);
  if (pr) return <PrereqDetail pr={pr} />;
  const fam = board.familien.find((f) => f.id === cardId);
  if (fam) return <FamDetail fam={fam} board={board} />;
  return (
    <div>
      <div className="mb-3"><BackButton fallback={backTo} label="Entscheidungen" /></div>
      <Card padding="none" className="p-4 text-sm text-text-3">Karte „{cardId}" nicht gefunden.</Card>
    </div>
  );
}

function BestandCard({ items }: { items?: Bestand[] }) {
  if (!items?.length) return null;
  return (
    <Card padding="none" className="p-4">
      <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Bestand heute</h5>
      <ul className="space-y-1.5">
        {items.map((b, i) => (
          <li key={i} className="text-sm text-text-2 flex gap-2">
            <span className="text-success shrink-0">✓</span>
            <span>{b.was}{b.evidenz && <span className="block text-xs text-text-3 mt-0.5">{b.evidenz}</span>}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
function LueckeCard({ items }: { items?: Luecke[] }) {
  if (!items?.length) return null;
  return (
    <Card padding="none" className="p-4">
      <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Fehlt noch</h5>
      <ul className="space-y-1.5">
        {items.map((l, i) => (
          <li key={i} className="text-sm text-text-2 flex gap-2">
            <span className="text-warning shrink-0">—</span>
            <span>{l.was}{l.effort && <span className="ml-1 text-xs text-text-3">(Aufwand {l.effort})</span>}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Kontext-Panel „Dokumente, die diese Entscheidung treiben" (P1.3) — filtert
 *  doc_typ_map nach der Karte (``zustaendige_karten``) bzw. Familie
 *  (``familien``). Ersetzt die geloeschte Top-Level-Dokumente-View; nichts geht
 *  verloren (Volumen, Verarbeitungspfad, SF-Bestand, Beispiel-Doc-Chips). */
function DokumenteKontext({ board, cardId, familieId }: {
  board: Board; cardId?: string; familieId?: string;
}) {
  const docs = (board.doc_typ_map || []).filter((d) =>
    (cardId != null && (d.zustaendige_karten || []).includes(cardId)) ||
    (familieId != null && (d.familien || []).includes(familieId)));
  if (docs.length === 0) return null;
  const sorted = [...docs].sort((a, b) => b.volumen_6m - a.volumen_6m);
  return (
    <Card padding="none" className="p-3.5 mt-4">
      <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">
        Dokumente, die diese Entscheidung treiben <span className="normal-case tracking-normal">— {docs.length} {docs.length === 1 ? "Typ" : "Typen"} · Klick öffnet Salesforce</span>
      </h5>
      <div className="space-y-2.5">
        {sorted.map((d) => (
          <div key={d.typ} className="border-l-2 border-brand-500/30 pl-2.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[0.86rem] font-medium text-text">{docLabel(d.typ)}</span>
              <span className="inline-flex items-center gap-1 tabular-nums text-[0.78rem] text-text-2">
                {fmtInt(d.volumen_6m)}
                <span className={clsx("inline-flex items-center px-1 rounded text-[0.6rem] font-bold",
                  d.volumen_quelle === "docs" ? "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300" : "bg-surface-3 text-text-3")}
                >{d.volumen_quelle}</span>
                <span className="text-text-3">/6M</span>
              </span>
              <PfadPill pfad={d.verarbeitungspfad} />
            </div>
            {d.sf_bestand && <div className="text-[0.76rem] text-text-3 leading-snug mt-0.5"><b className="text-text-2 font-medium">SF-Bestand:</b> {d.sf_bestand}</div>}
            {d.beispiel_docs?.length ? (
              <div className="mt-1 flex gap-1 flex-wrap">
                {d.beispiel_docs.slice(0, 6).map((dc) => <DocChip key={dc.id} doc={dc} />)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

function QwDetail({ qw, board }: { qw: QuickWin; board: Board }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qs = params.toString();
  const entHref = segHref(params, "entscheidungen");
  const { decisions } = useDecisions(true);
  const { statuses } = useCardStatuses(true);
  const status: CardStatus = statuses.find((s) => s.card_id === qw.id)?.status
    ?? (decisions.some((d) => d.card_id === qw.id) ? "entschieden" : "offen");
  // Reverse-Lookup: in welchen Familien-Soll-Prozessen ist diese Karte Baustein?
  const usedIn = board.familien.filter((f) =>
    (f.soll_prozess || []).some((s) => (s.baustein_refs || []).includes(qw.id)));
  const offen = (qw.entscheidungen_offen || []).filter((e) => e.frage);
  const savedH6 = qw.volumen?.saved_hours_6mo as number | undefined;
  const hy = savedH6 != null ? Math.round(savedH6 * 2) : null;
  const vh = volHint(qw);

  // Feste KPI-Reihenfolge: h/Jahr · Hebel · Aufwand · Status.
  const kpis = (
    <>
      {hy != null && <KpiChip label="h/Jahr" value={fmtInt(hy)} tone="brand" />}
      <KpiChip label="Hebel" value={qw.plattform} />
      <KpiChip label="Aufwand" value={(qw.effort_gesamt || "—").toUpperCase()} />
      <KpiChip label="Status" value={STATUS_LABEL[status]} tone={status === "offen" ? "warning" : "success"} />
    </>
  );

  return (
    <EntityPage
      backTo={entHref} backLabel="Entscheidungen"
      crumbs={[{ label: "Entscheidungen", to: entHref }, qw.titel]}
      kicker={
        <div className="mb-1.5 flex items-center gap-2 flex-wrap">
          <PlatBadge platform={qw.plattform} label={qw.plattform} />
          {qw.pattern && <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-text-2">{qw.pattern}</span>}
          {offen.length > 0 && <OpenBadge n={offen.length} />}
        </div>
      }
      title={qw.titel}
      subtitle={vh || undefined}
      kpis={kpis}
    >
      {qw.risiko && <p className="mb-4 text-sm text-text-2 leading-relaxed max-w-4xl"><b className="text-text">Risiko:</b> {qw.risiko}</p>}

      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <BestandCard items={qw.bestand} />
        <LueckeCard items={qw.luecke} />
      </div>

      {usedIn.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-text-3 mb-1.5">Baustein im Soll-Prozess von</div>
          <div className="flex gap-1.5 flex-wrap">
            {usedIn.map((f) => (
              <button key={f.id}
                onClick={() => navigate(`/email-automation/decide/${f.id}${qs ? `?${qs}` : ""}`)}
                className="inline-flex items-center px-2 py-0.5 rounded-md bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.72rem] font-semibold hover:bg-brand-500 hover:text-white transition-colors"
              >{f.id} · {f.name}</button>
            ))}
          </div>
        </div>
      )}

      {/* Dokumente-Kontext (P1.3) — welche Docs diese Entscheidung treiben */}
      <DokumenteKontext board={board} cardId={qw.id} />

      <DecisionBlock card={{ id: qw.id, kind: "quick_win", title: qw.titel }} questions={qw.entscheidungen_offen} />
    </EntityPage>
  );
}

function PrereqDetail({ pr }: { pr: Prereq }) {
  const [params] = useSearchParams();
  const entHref = segHref(params, "entscheidungen");
  const { decisions } = useDecisions(true);
  const { statuses } = useCardStatuses(true);
  const status: CardStatus = statuses.find((s) => s.card_id === pr.id)?.status
    ?? (decisions.some((d) => d.card_id === pr.id) ? "entschieden" : "offen");
  const u = pr.unlocks || {};
  const platLabel = pr.platform === "email-service" ? "email-service" : pr.platform === "atlas" ? "Atlas" : pr.platform === "portal" ? "Portal" : pr.platform;
  const unlockHy = pr.unlock_hours_per_year;

  const kpis = (
    <>
      {unlockHy != null && unlockHy > 0 && <KpiChip label="schaltet frei" value={`${fmtInt(unlockHy)} h/J`} tone="success" />}
      <KpiChip label="Aufwand" value={(pr.effort || "—").toUpperCase()} />
      <KpiChip label="Blocking" value={pr.blocking} tone={(pr.blocking || "").toLowerCase() === "hard" ? "danger" : "warning"} />
      <KpiChip label="Plattform" value={platLabel} />
      <KpiChip label="Status" value={STATUS_LABEL[status]} tone={status === "offen" ? "warning" : "success"} />
    </>
  );

  return (
    <EntityPage
      backTo={entHref} backLabel="Entscheidungen"
      crumbs={[{ label: "Entscheidungen", to: entHref }, `${pr.id} · ${pr.titel}`]}
      kicker={<div className="mb-1"><PlatBadge platform={pr.platform} label={platLabel} /></div>}
      title={`${pr.id} · ${pr.titel}`}
      kpis={kpis}
    >
      <div className="grid md:grid-cols-2 gap-3">
        <Card padding="none" className="p-4">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Beschreibung</h5>
          <p className="text-sm text-text-2 leading-relaxed">{pr.beschreibung_kurz}</p>
        </Card>
        <Card padding="none" className="p-4">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Schaltet frei</h5>
          <ul className="space-y-1.5 text-sm text-text-2">
            {u.agent_stufe2_cluster != null && <li>{u.agent_stufe2_cluster} Agent-Recipes (Stufe 2)</li>}
            {u.quick_wins?.length ? <li>Karten: {u.quick_wins.join(", ")}</li> : null}
            {u.patterns?.length ? <li>Patterns: {u.patterns.join(", ")}</li> : null}
            {!u.agent_stufe2_cluster && !u.quick_wins?.length && !u.patterns?.length && <li className="text-text-3">—</li>}
          </ul>
        </Card>
      </div>

      <DecisionBlock card={{ id: pr.id, kind: "prereq", title: `${pr.id} · ${pr.titel}` }} />
    </EntityPage>
  );
}

// ── Schritt-Ampel: rein abgeleitet aus Board-Daten (keine neuen Felder) ───────
type StepStatus = "gruen" | "gelb" | "rot";
interface StepDeriv {
  status: StepStatus;
  greenCards: string[];       // Quick-Win-IDs (entscheidbar/vorbereitet)
  offen: number;              // Summe offener Entscheidungen dieser Karten
  prs: string[];              // blockierende PR-IDs (aus refs + luecke-Text)
}
function offenCountOf(q: QuickWin): number {
  return (q.entscheidungen_offen || []).filter((e) => e.frage).length;
}
/** Deterministische Ampel je Soll-Schritt. GRÜN: ein baustein_ref löst auf eine
 *  entscheidbar/vorbereitet-Karte auf. ROT: PR-Referenz (in refs ODER luecke-Text).
 *  GELB: alles übrige (Teil-Stück existiert / assistierbar, kein PR-Blocker). */
function deriveStep(s: SollSchritt, board: Board): StepDeriv {
  const refs = s.baustein_refs || [];
  const luecke = s.luecke || "";
  const qwById = new Map(board.quick_wins.map((q) => [q.id, q] as const));
  const prIds = new Set(board.prerequisites.map((p) => p.id));
  const prs = Array.from(new Set([
    ...refs.filter((r) => prIds.has(r)),
    ...(luecke.match(/PR-\d/g) || []),
  ])).sort();
  const greenCards = refs.filter((r) => {
    const q = qwById.get(r);
    return q && (q.status === "entscheidbar" || q.status === "vorbereitet");
  });
  if (greenCards.length) {
    return { status: "gruen", greenCards, offen: greenCards.reduce((n, r) => n + offenCountOf(qwById.get(r)!), 0), prs };
  }
  if (prs.length) return { status: "rot", greenCards: [], offen: 0, prs };
  return { status: "gelb", greenCards: [], offen: 0, prs };
}

// Schritt-Ampel-Chip (grün bereit / gelb teilweise / rot Lücke).
function StepAmpel({ d }: { d: StepDeriv }) {
  if (d.status === "gruen") {
    return <span className="inline-flex items-center px-1.5 rounded bg-success/10 text-success text-[0.66rem] font-bold mt-0.5 shrink-0 whitespace-nowrap">
      bereit{d.offen > 0 ? ` — ${d.offen} ${d.offen === 1 ? "Frage" : "Fragen"} offen` : ""}
    </span>;
  }
  if (d.status === "gelb") {
    return <span className="inline-flex items-center px-1.5 rounded bg-warning/10 text-warning text-[0.66rem] font-bold mt-0.5 shrink-0">teilweise</span>;
  }
  return <span className="inline-flex items-center px-1.5 rounded bg-danger/10 text-danger text-[0.66rem] font-bold mt-0.5 shrink-0">Lücke</span>;
}
// Bau-Reihenfolge-Marker (grün baubar jetzt / rot nach PR-x); gelb ohne Marker.
function BauMarker({ d }: { d: StepDeriv }) {
  if (d.status === "gruen")
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-success/10 text-success text-[0.62rem] font-semibold whitespace-nowrap">baubar jetzt</span>;
  if (d.status === "rot")
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-danger/10 text-danger text-[0.62rem] font-semibold whitespace-nowrap">nach {d.prs.join(" · ")}</span>;
  return null;
}
// Klickbarer PR-Chip (führt zum PR-L2).
function PrChip({ id }: { id: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigate(`/email-automation/decide/${id}${params.toString() ? `?${params.toString()}` : ""}`); }}
      className="inline-flex items-center px-1.5 py-0.5 rounded bg-danger/10 text-danger text-[0.68rem] font-semibold whitespace-nowrap hover:bg-danger hover:text-white transition-colors"
    >{id}</button>
  );
}

function FamDetail({ fam, board }: { fam: Familie; board: Board }) {
  const [params] = useSearchParams();
  const entHref = segHref(params, "entscheidungen");
  const { decisions } = useDecisions(true);
  const { statuses } = useCardStatuses(true);
  const status: CardStatus = statuses.find((s) => s.card_id === fam.id)?.status
    ?? (decisions.some((d) => d.card_id === fam.id) ? "entschieden" : "offen");
  const vorfaelle = (board.vorfall_ketten?.top_vorfaelle || []).filter((v) =>
    (v.familien_refs || []).some((r) => r.familie === fam.id));

  // ── Schritt-Ampeln + Kopfblock „Nächste Aktionen" (rein abgeleitet) ─────────
  const steps = fam.soll_prozess || [];
  const derivs = steps.map((s) => deriveStep(s, board));
  // Jetzt entscheidbar: grüne Karten dedupliziert, mit Fragen-Zähler.
  const decidable = new Map<string, number>();
  derivs.forEach((d) => d.greenCards.forEach((c) => {
    const q = board.quick_wins.find((x) => x.id === c);
    if (q) decidable.set(c, offenCountOf(q));
  }));
  // Blockiert durch: PR -> betroffene Schritt-Nummern.
  const blockers = new Map<string, number[]>();
  derivs.forEach((d, i) => { if (d.status === "rot") d.prs.forEach((p) => { const a = blockers.get(p) || []; a.push(i + 1); blockers.set(p, a); }); });
  // human-only: gelbe Schritte ohne Baustein (assistierbar/menschlich).
  const humanSteps = derivs.map((d, i) => ({ d, i })).filter(({ d }) => d.status === "gelb").map(({ i }) => i + 1);
  const hasAnyBaustein = steps.some((s) => (s.baustein_refs || []).length);
  const showHead = steps.length > 0;

  const kpis = (
    <>
      <KpiChip label="h/Jahr" value={fmtInt(Math.round(fam.saved_hours_6mo * 2))} tone="brand" />
      <KpiChip label="Threads" value={fmtInt(fam.threads)} />
      <KpiChip label="Tasks" value={fmtInt(fam.tasks)} />
      <KpiChip label="Chase" value={`${fam.chase_quote.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`} />
      <KpiChip label="Status" value={STATUS_LABEL[status]} tone={status === "offen" ? "warning" : "success"} />
    </>
  );

  return (
    <EntityPage
      backTo={entHref} backLabel="Entscheidungen"
      crumbs={[{ label: "Entscheidungen", to: entHref }, fam.name]}
      kicker={<div className="mb-1"><TriageBadge triage={fam.triage} /></div>}
      title={`${fam.id} · ${fam.name}`}
      kpis={kpis}
    >
      {fam.triage_begruendung && (
        <p className="mb-4 text-sm text-text-2 leading-relaxed max-w-4xl">{fam.triage_begruendung}</p>
      )}

      {/* Kopfblock „Nächste Aktionen" — direkt aus Schritt-Ampeln abgeleitet, max 3 Zeilen */}
      {showHead && (
        <Card padding="none" className="p-3.5 mb-3">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Nächste Aktionen</h5>
          {decidable.size === 0 && blockers.size === 0 && !hasAnyBaustein ? (
            <div className="text-[0.82rem] text-text-2">Kein Baustein — Familie bleibt menschlich; Stütze: Reminder.</div>
          ) : (
            <div className="space-y-1.5">
              {decidable.size > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-[0.82rem]">
                  <span className="text-success font-semibold shrink-0">Jetzt entscheidbar:</span>
                  {Array.from(decidable.entries()).map(([id, n]) => (
                    <span key={id} className="inline-flex items-center gap-1">
                      <BausteinChip id={id} board={board} />
                      <span className="text-text-3 text-[0.7rem]">{n > 0 ? `${n} offen` : "0 offen"}</span>
                    </span>
                  ))}
                </div>
              )}
              {blockers.size > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap text-[0.82rem]">
                  <span className="text-danger font-semibold shrink-0">Blockiert durch:</span>
                  {Array.from(blockers.entries()).map(([pr, nums]) => (
                    <span key={pr} className="inline-flex items-center gap-1">
                      <PrChip id={pr} />
                      <span className="text-text-3 text-[0.7rem]">Schritt {nums.join(", ")}</span>
                    </span>
                  ))}
                </div>
              )}
              {decidable.size === 0 && humanSteps.length > 0 && (
                <div className="text-[0.82rem] text-text-2">
                  <span className="text-text-3 font-semibold">human-only:</span> Schritt {humanSteps.join(", ")}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Soll-Prozess (kuratiert) — kompakt: Schritt + Ampel + Baustein/PR + Lücke inline */}
      {fam.soll_prozess?.length ? (
        <Card padding="none" className="p-3 mb-2">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Soll-Prozess <span className="normal-case tracking-normal">— {fam.soll_prozess.length} Schritte · Bausteine &amp; SF-Links klickbar</span></h5>
          <ol className="space-y-1.5">
            {fam.soll_prozess.map((s, i) => {
              const d = derivs[i];
              return (
              <li key={i} className="flex gap-2.5">
                <span className="w-5 h-5 rounded-full bg-surface-3 text-text-2 text-[0.68rem] font-bold grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <StepAmpel d={d} />
                    <span className="text-[0.86rem] font-medium text-text">{s.schritt}</span>
                    <PfadPill pfad={s.processing_path} />
                    {(s.doc_types || []).map((dt) => (
                      <span key={dt} className="inline-flex items-center px-1.5 rounded bg-surface-3 text-text-2 text-[0.64rem] font-medium whitespace-nowrap">{docLabel(dt)}</span>
                    ))}
                    {s.akteur_von_an && <span className="text-[0.7rem] text-text-3 whitespace-nowrap">{akteurLabel(s.akteur_von_an)}</span>}
                    {(s.baustein_refs || []).filter((r) => !r.startsWith("PR-")).map((r) => <BausteinChip key={r} id={r} board={board} />)}
                    <BauMarker d={d} />
                    {d.status === "rot" && d.prs.map((p) => <PrChip key={p} id={p} />)}
                  </div>
                  {s.luecke && (
                    <div className="text-[0.76rem] text-text-3 leading-snug">
                      <span className="text-text-2">{s.luecke}</span>
                      {s.evidenz && <span className="text-text-3"> · {s.evidenz}</span>}
                    </div>
                  )}
                </div>
              </li>
              );
            })}
          </ol>
        </Card>
      ) : null}

      {/* Kompakt-Leiste: Rollen/Pfade · Ausgang · Auslöser · Checkliste in EINER Karte */}
      {(fam.roles?.length || fam.trigger_classes?.length || fam.outcomes || fam.checklist_top?.length) && (
        <Card padding="none" className="p-3 mb-2">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
            {(fam.roles?.length || fam.processing_paths?.length) ? (
              <div className="min-w-0">
                <h5 className="text-[0.64rem] uppercase tracking-wide text-text-3 mb-1">Wer treibt · Pfade</h5>
                <div className="flex gap-1 flex-wrap">
                  {(fam.roles || []).slice(0, 4).map((r) => (
                    <span key={r.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-3 text-text-2 text-[0.68rem]">{r.key} <b className="tabular-nums text-text">{r.n}</b></span>
                  ))}
                  {(fam.processing_paths || []).slice(0, 3).map((p) => (
                    <span key={p.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border-strong text-[0.64rem] text-text-2">{PATH_LABEL[p.key] || p.key} <b className="tabular-nums">{p.n}</b></span>
                  ))}
                </div>
              </div>
            ) : null}
            {fam.outcomes && (
              <div className="min-w-0">
                <h5 className="text-[0.64rem] uppercase tracking-wide text-text-3 mb-1">Ausgang</h5>
                <div className="flex items-center gap-3 text-[0.82rem]">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success" /> <b className="tabular-nums text-text">{fmtInt(fam.outcomes.resolved ?? 0)}</b> <span className="text-text-3">gelöst</span></span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" /> <b className="tabular-nums text-text">{fmtInt(fam.outcomes.open ?? 0)}</b> <span className="text-text-3">offen</span></span>
                </div>
                {vorfaelle.length > 0 && (
                  <div className="mt-1 text-[0.7rem] text-text-3">{vorfaelle.length} Vorfall-{vorfaelle.length === 1 ? "Kette" : "Ketten"} als Beleg unten ↓</div>
                )}
              </div>
            )}
            {fam.trigger_classes?.length ? (
              <div className="min-w-0">
                <h5 className="text-[0.64rem] uppercase tracking-wide text-text-3 mb-1">Auslöser</h5>
                <div className="space-y-0.5">
                  {fam.trigger_classes.slice(0, 4).map((c) => (
                    <div key={c.key} className="grid grid-cols-[minmax(0,1fr)_28px] gap-1.5 items-center text-[0.72rem] text-text-2">
                      <span className="truncate" title={c.key}>{c.key}</span>
                      <span className="text-right tabular-nums">{c.n}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {fam.checklist_top?.length ? (
              <div className="min-w-0">
                <h5 className="text-[0.64rem] uppercase tracking-wide text-text-3 mb-1">Checkliste</h5>
                <div className="space-y-0.5">
                  {fam.checklist_top.slice(0, 4).map((c, i) => (
                    <div key={i} className="grid grid-cols-[minmax(0,1fr)_30px] gap-1.5 items-center text-[0.72rem] text-text-2">
                      <span className="truncate" title={c.item}>{c.item}</span>
                      <span className="text-right tabular-nums">{c.anteil}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      )}

      {/* Beispiel-Threads — direkt sichtbar, mit SF-Durchgriff (Thread-Link + Doc-Chips) */}
      {fam.beispiele?.length ? (
        <Card padding="none" className="p-3 mb-2">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-2">Beispiel-Threads &amp; Dokumente <span className="normal-case tracking-normal">— Klick öffnet Salesforce</span></h5>
          <div className="space-y-2">
            {fam.beispiele.map((b, i) => (
              <div key={i} className="border-l-2 border-brand-500/40 pl-2.5">
                <div className="flex items-start gap-2 flex-wrap">
                  {b.sf_url && <SfLink url={b.sf_url} label="Thread" title={b.thread_id} />}
                  {b.marker === "mit_dokumenten" && (
                    <span className="inline-flex items-center rounded-full bg-brand-500/12 px-1.5 py-0.5 text-[0.62rem] font-medium uppercase tracking-wide text-brand-500">mit Dokumenten</span>
                  )}
                  {b.trigger && <span className="text-[0.84rem] text-text flex-1 min-w-0">{b.trigger}</span>}
                  {b.marker === "mit_dokumenten" && b.subject && (
                    <span className="text-[0.84rem] text-text-2 flex-1 min-w-0 truncate">{b.subject}</span>
                  )}
                </div>
                {b.trigger_evidence && (
                  <blockquote className="mt-0.5 text-[0.78rem] text-text-3 italic">„{b.trigger_evidence}"</blockquote>
                )}
                {b.outcome && <div className="mt-0.5 text-[0.74rem] text-text-3"><b className="text-text-2 font-medium">Ausgang:</b> {b.outcome}</div>}
                {b.docs?.length ? (
                  <div className="mt-1 flex gap-1 flex-wrap">
                    {b.docs.map((dc) => <DocChip key={dc.id} doc={dc} />)}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      {fam.kernablauf?.length ? (
        <Card padding="none" className="p-3 mb-2">
          <h5 className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-1.5">Kernablauf</h5>
          <ul className="space-y-1 text-[0.84rem] text-text-2 list-disc pl-5">
            {fam.kernablauf.map((k, i) => <li key={i}>{k}</li>)}
          </ul>
        </Card>
      ) : null}
      {fam.bausteine?.length ? (
        <div className="mt-2 text-xs text-text-3">Bausteine: {fam.bausteine.join(" · ")}</div>
      ) : null}

      {/* „So zerfasert das in der Praxis" — Vorfall-Ketten als Beleg-Block (P1.2;
          war frueher eine eigene Top-Level-View). Jede Zeile = ein realer
          Mehr-Thread-Vorgang dieser Familie; Klick klappt die Threads mit
          SF-Durchgriff auf. */}
      {vorfaelle.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-3">
            <span>So zerfasert das in der Praxis</span>
            <span className="rounded-full bg-surface-3 text-text-2 px-2 py-0.5 font-medium normal-case tracking-normal">{vorfaelle.length}</span>
            <span className="ml-auto font-medium normal-case tracking-normal text-text-3">Mehr-Thread-Vorgänge · Klick öffnet die Threads (Salesforce)</span>
          </div>
          <Card padding="none" className="overflow-hidden">
            {vorfaelle.map((v) => <VorfallRow key={v.vorfall_id} v={v} />)}
          </Card>
        </div>
      )}

      {/* Dokumente-Kontext (P1.3) — welche Docs diese Familie treiben */}
      <DokumenteKontext board={board} familieId={fam.id} />

      <DecisionBlock card={{ id: fam.id, kind: "familie", title: `${fam.id} · ${fam.name}` }} />
    </EntityPage>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Vorfall-Ketten — Beleg-Block-Komponente (P1.2: keine eigene Top-Level-View
// mehr; VorfallRow wird in der Familien-L2 „So zerfasert das in der Praxis"
// wiederverwendet).
// ═════════════════════════════════════════════════════════════════════════════
function fmtSpan(z?: { start?: string; end?: string; days?: number }): string {
  if (!z) return "—";
  const d = (iso?: string) => {
    if (!iso) return "?";
    const dt = new Date(iso);
    return isNaN(dt.getTime()) ? iso.slice(0, 10) : dt.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  };
  return `${d(z.start)}–${d(z.end)}${z.days != null ? ` · ${z.days} T` : ""}`;
}

// Vorfall-Zeile — feste Grid-Spalten: threads · accounts · zweck · zeitspanne · depts · chevron.
function VorfallRow({ v, hero, defaultOpen = false }: { v: Vorfall; hero?: boolean; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  return (
    <div className={clsx("border-t border-border first:border-t-0", hero && "bg-brand-50 dark:bg-brand-500/10")}>
      <div
        onClick={() => setOpen((o) => !o)} {...keyActivate(() => setOpen((o) => !o))} aria-expanded={open}
        aria-label={v.accounts.length ? v.accounts.join(", ") : v.vorfall_id}
        className={clsx(
          "grid items-center gap-2.5 px-3.5 py-2.5 cursor-pointer",
          hero ? "hover:bg-brand-100 dark:hover:bg-brand-500/15" : "hover:bg-surface-2",
          "grid-cols-[64px_minmax(0,1fr)_auto] md:grid-cols-[64px_180px_minmax(0,1fr)_128px_110px_16px]",
        )}
      >
        <span className={clsx(
          "inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[0.72rem] font-bold tabular-nums whitespace-nowrap justify-self-start",
          hero ? "bg-brand-500 text-white" : "bg-surface-3 text-text-2",
        )}>{v.thread_count} Thr</span>
        <span className="text-sm font-medium text-text truncate">{v.accounts.length ? v.accounts.join(", ") : "—"}</span>
        <span className="hidden md:block text-[0.8rem] text-text-3 truncate" title={v.zweck_1zeile || v.match_terms.join(" · ")}>{v.zweck_1zeile || v.match_terms.join(" · ") || "—"}</span>
        <span className="hidden md:block text-[0.8rem] text-text-3 tabular-nums whitespace-nowrap">{fmtSpan(v.zeitspanne)}</span>
        <span className="hidden md:flex gap-1 flex-wrap justify-self-start">
          {v.depts.map((d) => <span key={d} className="px-1.5 py-0.5 rounded bg-surface-3 text-text-2 text-[0.68rem] font-medium">{d}</span>)}
        </span>
        {open
          ? <ChevronDown className="hidden md:block w-4 h-4 text-text-3" />
          : <ChevronRight className="hidden md:block w-4 h-4 text-text-3" />}
      </div>
      {open && (
        <div className="px-3.5 pb-3 pt-1">
          <div className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-1.5">
            {v.vorfall_id} · {v.task_count != null ? `${fmtInt(v.task_count)} Tasks · ` : ""}Threads chronologisch (max 6)
          </div>
          {(v.familien_refs?.length || v.match_terms.length) ? (
            <div className="mb-2 flex items-center gap-1.5 flex-wrap">
              {(v.familien_refs || []).map((r) => (
                <button key={r.familie}
                  onClick={(e) => { e.stopPropagation(); navigate(`/email-automation/decide/${r.familie}${params.toString() ? `?${params.toString()}` : ""}`); }}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.68rem] font-semibold hover:bg-brand-500 hover:text-white transition-colors"
                  title={`${r.overlap} gemeinsame Threads`}
                >{r.familie} ({r.overlap})</button>
              ))}
              {v.match_terms.length > 0 && (
                <span className="text-[0.72rem] text-text-3">{v.match_terms.join(" · ")}</span>
              )}
            </div>
          ) : null}
          <div className="space-y-1.5">
            {v.thread_subjects.map((s, i) => (
              <div key={i} className="grid grid-cols-[76px_minmax(0,1fr)] gap-2 text-[0.82rem] items-start">
                <span className="text-text-3 tabular-nums pt-0.5">{new Date(s.date).toLocaleDateString("de-DE")}</span>
                <div className="min-w-0">
                  <div className="flex items-start gap-1.5 flex-wrap">
                    {s.sf_url && <SfLink url={s.sf_url} label="SF" title={s.thread_id} />}
                    <span className="text-text-2 min-w-0 flex-1" title={s.subject}>{s.subject}</span>
                  </div>
                  {s.docs?.length ? (
                    <div className="mt-0.5 flex gap-1 flex-wrap">
                      {s.docs.map((dc) => <DocChip key={dc.id} doc={dc} />)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {v.thread_subjects.length === 0 && <div className="text-xs text-text-3">Keine Betreffs verfügbar.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// L2: Agent-Stufe — alle Cluster der Stufe, expandierbar, Reassigns unten
// ═════════════════════════════════════════════════════════════════════════════
const STAGE_META: Record<number, { label: string; badge: string }> = {
  1: { label: "Stufe 1 · Polling/Reminder — heute live", badge: "HEUTE LIVE" },
  2: { label: "Stufe 2 · Recipe-Workflows", badge: "NÄCHSTER SCHRITT" },
  3: { label: "Stufe 3 · Autonom (echtes Fachurteil)", badge: "ZIELBILD" },
};

function CapBadge({ cap }: { cap: string }) {
  const hard = cap === "mail_send" || cap === "doc_extract";
  return (
    <span className={clsx(
      "inline-flex items-center px-1.5 py-0.5 rounded text-[0.66rem] font-medium whitespace-nowrap",
      hard ? "bg-warning/10 text-warning" : "bg-surface-3 text-text-2",
    )} title={hard ? "geblockt: braucht PR-1/PR-2 (mail_send) bzw. PR-4 (doc_extract)" : undefined}>{cap}</span>
  );
}

function AgentStageDetail({ board, stufe }: { board: Board; stufe: number }) {
  const [params] = useSearchParams();
  const sp = new URLSearchParams(params);
  sp.set("seg", "roadmap");
  const backTo = `/email-automation?${sp.toString()}`;
  const all = (board.agent_roadmap.clusters || []).filter((c) => c.stage === stufe);
  const main = all.filter((c) => !c.reassign_suggestion)
    .sort((a, b) => (b.saved_hours_6mo ?? 0) - (a.saved_hours_6mo ?? 0));
  const reassigns = all.filter((c) => c.reassign_suggestion)
    .sort((a, b) => (b.saved_hours_6mo ?? 0) - (a.saved_hours_6mo ?? 0));
  const meta = STAGE_META[stufe];
  const stufeInfo = board.agent_roadmap.stufen.find((s) => s.stufe === stufe);
  const sumH = all.reduce((s, c) => s + (c.saved_hours_6mo ?? 0), 0);
  const withRecipe = all.filter((c) => c.recipe_sketch).length;

  if (!meta || all.length === 0) {
    return (
      <div>
        <div className="mb-3"><BackButton fallback={backTo} label="Agent-Roadmap" /></div>
        <Card padding="none" className="p-4 text-sm text-text-3">Keine Cluster für Stufe {stufe}.</Card>
      </div>
    );
  }

  const kpis = (
    <>
      <KpiChip label="Cluster" value={all.length} tone="brand" />
      <KpiChip label="h/Jahr" value={fmtInt(Math.round(sumH * 2))} />
      <KpiChip label="mit Rezept" value={`${withRecipe}/${all.length}`} tone="success" />
      {reassigns.length > 0 && <KpiChip label="Reassign" value={reassigns.length} tone="danger" />}
    </>
  );

  return (
    <EntityPage
      backTo={backTo} backLabel="Agent-Roadmap"
      crumbs={[{ label: "Agent-Roadmap", to: backTo }, meta.label]}
      kicker={<div className="mb-1"><span className="inline-flex items-center px-2 py-0.5 rounded-md text-[0.72rem] font-bold bg-surface-3 text-text-2">{meta.badge}</span></div>}
      title={meta.label}
      subtitle={stufeInfo?.name}
      kpis={kpis}
    >
      <GroupHead label="Cluster" count={main.length} note="sortiert nach h/6M" />
      <Card padding="none" className="overflow-hidden">
        {main.map((c) => <AgentClusterRow key={c.id} c={c} />)}
      </Card>

      {reassigns.length > 0 && (
        <>
          <GroupHead label="Reassigns — besser bei anderem Hebel" count={reassigns.length} tone="danger" />
          <Card padding="none" className="overflow-hidden">
            {reassigns.map((c) => <AgentClusterRow key={c.id} c={c} />)}
          </Card>
        </>
      )}
    </EntityPage>
  );
}

// Cluster-Zeile — feste Grid-Spalten: name · dept · h/6M · capabilities · chevron.
// Klick klappt Details auf (Expand, kein neues L2).
function AgentClusterRow({ c }: { c: AgentCluster }) {
  const [open, setOpen] = useState(false);
  const rs = c.recipe_sketch;
  // Deutsche Felder bevorzugen, sonst engl. Fallback (separates JSON-Update).
  const rsTrigger = rs?.trigger_de ?? rs?.trigger;
  const rsSteps = rs?.steps_de ?? rs?.steps ?? [];
  const rsEnds = rs?.endstates_de ?? rs?.end_states ?? [];
  return (
    <div className="border-t border-border first:border-t-0">
      <div
        onClick={() => setOpen((o) => !o)} {...keyActivate(() => setOpen((o) => !o))} aria-expanded={open}
        aria-label={c.name_de ?? c.name}
        className={clsx(
          "grid items-center gap-2.5 px-3.5 py-2.5 cursor-pointer hover:bg-surface-2",
          "grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_72px_72px_260px_16px]",
        )}
      >
        <span className="text-sm font-medium text-text truncate" title={c.id}>{c.name_de ?? c.name}</span>
        <span className="hidden md:inline-flex px-1.5 py-0.5 rounded bg-surface-3 text-text-2 text-[0.68rem] font-medium justify-self-start">{c.dept}</span>
        <span className="hidden md:block text-sm text-text-2 tabular-nums text-right">{c.saved_hours_6mo != null ? c.saved_hours_6mo.toLocaleString("de-DE", { maximumFractionDigits: 1 }) : "—"} h</span>
        <span className="hidden md:flex gap-1 flex-wrap justify-self-start">
          {c.required_capabilities.map((cap) => <CapBadge key={cap} cap={cap} />)}
        </span>
        {open
          ? <ChevronDown className="hidden md:block w-4 h-4 text-text-3" />
          : <ChevronRight className="hidden md:block w-4 h-4 text-text-3" />}
      </div>
      {open && (
        <div className="px-3.5 pb-3.5 pt-0.5 space-y-2.5">
          <div className="text-[0.68rem] text-text-3">{c.id}{c.freq != null ? ` · ${fmtInt(c.freq)} Vorgänge/6M` : ""}</div>
          {rs && (
            <div className="rounded-lg border border-border bg-surface-2 px-3 py-2.5">
              <div className="text-[0.68rem] uppercase tracking-wide text-text-3 mb-1.5">Recipe-Skizze</div>
              {rsTrigger && <div className="text-[0.82rem] text-text-2 mb-1.5"><b className="text-text font-medium">Trigger:</b> {rsTrigger}</div>}
              {rsSteps.length ? (
                <ol className="list-decimal pl-5 space-y-0.5 text-[0.82rem] text-text-2">
                  {rsSteps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              ) : null}
              {rsEnds.length ? (
                <div className="mt-1.5 text-[0.8rem] text-text-3"><b className="text-text-2 font-medium">Endzustände:</b> {rsEnds.join(" · ")}</div>
              ) : null}
            </div>
          )}
          {(c.urteil_bleibt_human_de ?? c.urteil_bleibt_human) && (
            <div className="flex items-start gap-1.5 text-[0.82rem]">
              <span className="inline-flex items-center px-1.5 rounded bg-warning/10 text-warning text-[0.66rem] font-bold mt-0.5 shrink-0">human</span>
              <span className="text-text-2">{c.urteil_bleibt_human_de ?? c.urteil_bleibt_human}</span>
            </div>
          )}
          {c.warum_autonom && (
            <div className="text-[0.82rem] text-text-2"><b className="text-text font-medium">Warum autonom:</b> {c.warum_autonom_de ?? c.warum_autonom}</div>
          )}
          {c.heutiger_ablauf && (
            <div className="text-[0.82rem] text-text-2"><b className="text-text font-medium">Heutiger Ablauf:</b> {c.heutiger_ablauf}</div>
          )}
          {c.stage1_scope && !rs && !c.heutiger_ablauf && (
            <div className="text-[0.82rem] text-text-2"><b className="text-text font-medium">Heutiger Umfang:</b> {c.stage1_scope}</div>
          )}
          {c.teil2_recipe_vorziehbar && !rs && (
            <div className="text-[0.82rem] text-text-2"><b className="text-text font-medium">Vorziehbar:</b> {c.teil2_recipe_vorziehbar}</div>
          )}
          {c.reassign_suggestion && (
            <div className="text-[0.82rem] text-danger"><b className="font-medium">Reassign:</b> {c.begruendung_ausfuehrlich || c.reassign_suggestion_de || c.reassign_suggestion}</div>
          )}
          {c.rationale && <div className="text-[0.8rem] text-text-3 italic">{c.rationale_de ?? c.rationale}</div>}
        </div>
      )}
    </div>
  );
}